import { Menu } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Outlet, useLocation, useParams } from 'react-router'

import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from 'components/ui/sheet'
import { partnersQuoteRequest } from 'rails_routes'
import { IconButton, StepNav, useBreakpoint } from 'slab'

import { ProspectProvider, useProspect } from './ProspectProvider'

import { QuoteRequestStep } from '.'

interface QuoteReviewStep {
  label: string
  active: boolean
  completed: boolean
  path?: string
  steps?: QuoteReviewStep[]
}

interface InputStep {
  label: string
  path: string
  hidden?: boolean
  steps?: InputStep[]
}

interface QuoteRequestLayoutProps {
  dashboardUrl: string
}

const DashboardLink = ({ url }: { url: string }) => (
  <a
    href={url}
    className='text-sm font-normal text-gray-500 hover:text-gray-800'
  >
    ← Back to dashboard
  </a>
)

function QuoteRequestNav () {
  const { id } = useParams()
  const { prospect } = useProspect()
  const location = useLocation()
  const steps: QuoteReviewStep[] = useMemo(() => {
    const initialSteps: InputStep[] = [
      { label: 'Basic details', path: QuoteRequestStep.CLIENT_DETAILS },
      {
        label: 'Upload files',
        path: '/upload',
        steps: [
          { label: 'Census', path: QuoteRequestStep.CENSUS_UPLOAD },
          { label: 'SBCs', path: QuoteRequestStep.SBCS_UPLOAD, hidden: !prospect?.offers_health_insurance_today },
          { label: 'Other', path: QuoteRequestStep.OTHER_FILES_UPLOAD },
        ]
      },
      { label: 'Summary', path: QuoteRequestStep.SUMMARY },
    ]

    let foundActiveStep = false
    const getStepsFromLocation = (initialSteps?: InputStep[]): QuoteReviewStep[] => {
      return initialSteps?.filter(step => !step.hidden).map(step => {
        const { label, path, steps } = step
        const fullPath = `${partnersQuoteRequest.show({ id })}/${path}`

        if (foundActiveStep) {
          return {
            active: false,
            completed: false,
            path: fullPath,
            label
          }
        }

        const active = location.pathname.startsWith(fullPath)
        foundActiveStep = active
        return {
          active,
          completed: !active,
          path: fullPath,
          label,
          steps: getStepsFromLocation(steps)
        }
      }) ?? []
    }

    return getStepsFromLocation(initialSteps)
  }, [location, prospect])

  return (
    <>
      <div className='p-6'>
        <StepNav data-testid='step-nav'>
          {steps.map((step) => (
            <StepNav.Step
              key={step.label}
              name={step.label}
              active={step.active}
              complete={step.completed}
              available={step.completed}
              href={step.path}
            >
              {(step.active || step.completed) && step.steps?.length && step.steps?.map((subStep) =>
                <StepNav.Step
                  key={subStep.label}
                  name={subStep.label}
                  active={subStep.active}
                  complete={subStep.completed}
                  available={subStep.completed}
                  href={subStep.path}
                />
              )}
            </StepNav.Step>
          ))}
        </StepNav>
      </div>
    </>
  )
}

function QuoteRequestLayoutImpl ({
  dashboardUrl,
}: QuoteRequestLayoutProps) {
  const { prospect } = useProspect()
  const { breakpointIsAtMost, breakpointIsAtLeast } = useBreakpoint()
  const [navOpen, setNavOpen] = useState(false)

  return (
    <div className='flex flex-col'>
      {breakpointIsAtMost('sm') &&
        <div data-controller='navbar' className='sticky top-0 flex h-14 shadow backdrop-blur-xl'>
          <div className='flex items-center justify-center border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500'>
            <IconButton label='Open sidebar' icon={Menu} onClick={() => setNavOpen(!navOpen)} />
          </div>
        </div>}
      <Sheet open={navOpen} onOpenChange={setNavOpen}>
        <SheetContent side='left' className='w-64'>
          <SheetTitle className='mb-6'>
            <DashboardLink url={dashboardUrl} />
          </SheetTitle>
          <hr className='absolute left-0 right-0 shadow' />
          <SheetDescription hidden>Navigation sidebar for quote request flow</SheetDescription>
          <QuoteRequestNav />
        </SheetContent>
      </Sheet>
      <div className='flex min-h-screen'>
        {breakpointIsAtLeast('md') &&
          <aside className='w-64 border-r border-gray-200 bg-gray-50'>
            <div className='p-6'>
              <DashboardLink url={dashboardUrl} />
            </div>
            <hr />
            <QuoteRequestNav />
          </aside>}

        <main className='flex-1 p-8'>
          {prospect ? <Outlet /> : <LoadingSpinnerCentered />}
        </main>
      </div>
    </div>
  )
}

export function QuoteRequestLayout (props: QuoteRequestLayoutProps) {
  const { id } = useParams()

  return (
    <ProspectProvider id={id ?? ''}>
      <QuoteRequestLayoutImpl {...props} />
    </ProspectProvider>
  )
}
